import { flushSync } from "react-dom";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { VOICE_MODE, VOICE_NOTES } from "../../constant/voiceMode";
import { iChatData } from "../../types";
import { voiceBus } from "../../services/voice/voiceBus";
import { Exchange, Fragment, joinFragments } from "../../services/voice/liveTranscript";
import { decideDelegation } from "../../services/voice/voiceFrames";
import type { HandOff, VoiceSession } from "./types";
import { VoiceRuntime, voiceLog, now, isRequestRunning, sendAppend } from "./runtime";
import type { TranscriptIO } from "./transcriptIO";
import type { Bubbles } from "./bubbles";
import type { Exchanges } from "./exchanges";

export const createHandOffs = (rt: VoiceRuntime, transcriptIO: TranscriptIO, bubbles: Bubbles, exchanges: Exchanges) => {
  const { live, turnRef } = rt;
  const { writeUserLines, writeDelegation, adoptChatId, writeJoined, cleanerOf } = transcriptIO;
  const { applyTargets, dropEarlyRequest } = bubbles;
  const { fileExchanges } = exchanges;

  const recordBusy = (session: VoiceSession, pending: HandOff, user: Fragment[], text: string) => {
    session.stats.busy += 1;
    user.forEach((fragment) => session.busy.add(fragment));
    writeUserLines(session, user, "gpt-live", pending.id, null);
    writeDelegation(session.id, {
      id: pending.id,
      offsetMs: pending.offsetMs,
      requestText: text,
      traceId: null,
      outcome: "busy",
      stoppedByUser: false,
    });
    writeJoined(session, pending, null, "busy");
  };

  const handOff = (session: VoiceSession, pending: HandOff, exchange: Exchange, text: string, hidden = false) => {
    if (session.filings.length > 0) flushSync(() => fileExchanges(session, true));
    const { poc: sender, state: current } = live.current;
    const draft = current.chatInputText;
    session.lastExchange = null;
    const newChat = current.chatData.length === 0;
    const sent = newChat
      ? sender.handleNewTripSendMessage(text, hidden, { voice: true })
      : sender.handleTopDestinationSendMessage(text, hidden, false, { voice: true });
    if (draft) current.setChatInputText(draft);
    adoptChatId(session.id);
    const turn = turnRef.current;
    const request = voiceBus.activeRequest();
    if (!turn || !turn.voice || !turn.open || !request || request.traceId !== turn.traceId) {
      sendAppend(session, "session.commentary.append", pending.id, VOICE_NOTES.busy);
      recordBusy(session, pending, exchange.user, text);
      voiceLog("request.refused", { id: pending.id });
      return;
    }
    turn.draft = draft;
    turn.clean = { fragments: exchange.user, raw: text };
    turn.cleaner = cleanerOf(session);
    turn.delegation = {
      id: pending.id,
      offsetMs: pending.offsetMs,
      requestText: text,
      traceId: turn.traceId,
      outcome: "sent",
      stoppedByUser: false,
    };
    turn.liveSessionId = session.id;
    turn.handOffAt = now();
    turn.words = [...exchange.reply];
    turn.timing = { at: pending.at };
    session.windowTurn = turn;
    writeUserLines(session, exchange.user, "xenia", pending.id, turn.traceId);
    writeDelegation(session.id, turn.delegation);
    writeJoined(session, pending, turn.traceId, "sent");
    const waitMs = now() - pending.at;
    voiceLog("request.sent", { id: pending.id, traceId: turn.traceId, ms: waitMs });
    session.stats.requests += 1;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_voice_request_sent, "", "", "", {
      source: "spoken",
      new_chat: newChat,
      wait_ms: waitMs,
      reclaimed: hidden,
    });
    void Promise.resolve(sent).then((result: any) => {
      if (!result?.userEntryId) return;
      turn.userEntryId = result.userEntryId;
      session.targets.push({ fragments: exchange.user, entryId: result.userEntryId, sent: text, shown: null, filed: false });
      applyTargets(session);
    });
    closeLateLine(session);
    cleanerOf(session).flush("user", exchange.user);
    session.lateLine = {
      turn,
      fragments: exchange.user,
      at: now(),
      last: exchange.user[exchange.user.length - 1],
      pending,
      capped: now() - (session.lastUserAt ?? 0) < VOICE_MODE.handOffQuietMs,
    };
  };

  const closeLateLine = (session: VoiceSession) => {
    const line = session.lateLine;
    if (!line) return;
    session.lateLine = null;
    cleanerOf(session).finish("user", line.fragments);
  };

  const joinsHandOff = (session: VoiceSession, fragment: Fragment, at: number): boolean => {
    const line = session.lateLine;
    if (!line || at - line.at > VOICE_MODE.lateWordsMs || fragment.startMs - line.last.endMs > VOICE_MODE.quietMs) return false;
    line.fragments.push(fragment);
    line.last = fragment;
    line.turn.late.push(fragment);
    writeUserLines(session, [fragment], "gpt-live", line.pending.id, null);
    return true;
  };

  const reclaimExchange = (session: VoiceSession, pending: HandOff, exchange: Exchange): boolean => {
    if (session.filings.length > 0) flushSync(() => fileExchanges(session, true));
    const last = session.lastExchange;
    const chat: iChatData[] = live.current.state.chatData;
    if (!last || session.transcript.pendingText()) return false;
    if (chat[chat.length - 2]?.id !== last.userId || chat[chat.length - 1]?.id !== last.replyId) return false;
    const words = [...last.fragments, ...exchange.user];
    const text = joinFragments(words);
    flushSync(() => live.current.state.setChatData((prev: iChatData[]) => prev.filter((entry) => entry.id !== last.replyId)));
    voiceLog("request.reclaimed", { id: pending.id, chars: text.length });
    handOff(session, pending, { user: words, reply: exchange.reply }, text, true);
    return true;
  };

  const checkHandOff = (session: VoiceSession) => {
    const pending = session.handOff;
    if (!pending) return;
    if (!session.transcript.handOffReady(pending.offsetMs) && now() - pending.at < VOICE_MODE.settleMs) return;
    const busy = pending.busy || isRequestRunning();
    const talking = now() - (session.lastUserAt ?? 0) < VOICE_MODE.handOffQuietMs;
    if (!busy && talking && now() - pending.at < VOICE_MODE.handOffMaxHoldMs) return;
    session.handOff = null;
    const heard = busy ? session.transcript.holdBusy(pending.offsetMs) : [];
    if (busy && heard.length === 0 && pending.late) {
      writeDelegation(session.id, { id: pending.id, offsetMs: pending.offsetMs, requestText: "", traceId: pending.late, outcome: "sent", stoppedByUser: false });
      writeJoined(session, pending, pending.late, "sent");
      voiceLog("delegation.joined", { id: pending.id, traceId: pending.late });
      return;
    }
    session.windowTurn = null;
    if (busy) {
      if (heard.length > 0) session.heldLine = { at: now(), pending, last: heard[heard.length - 1] };
      if (!pending.busy) sendAppend(session, "session.commentary.append", pending.id, VOICE_NOTES.busy);
      recordBusy(session, pending, heard, joinFragments(heard));
      return;
    }
    const exchange = session.transcript.takeHandOff(Number.POSITIVE_INFINITY);
    dropEarlyRequest(session);
    const text = joinFragments(exchange.user);
    const outcome = decideDelegation(text, false);
    if (outcome === "empty") {
      if (reclaimExchange(session, pending, exchange)) return;
      sendAppend(session, "session.thinking.append", pending.id, VOICE_NOTES.nothingHeard);
      voiceLog("request.empty", { id: pending.id });
      session.stats.empty += 1;
      return;
    }
    handOff(session, pending, exchange, text);
  };

  const joinsHeldLine = (session: VoiceSession, fragment: Fragment, at: number): boolean => {
    const line = session.heldLine;
    if (!line || at - line.at > VOICE_MODE.lateWordsMs || fragment.startMs - line.last.endMs > VOICE_MODE.quietMs) return false;
    if (!session.transcript.joinBusy(fragment)) return false;
    line.last = fragment;
    session.busy.add(fragment);
    writeUserLines(session, [fragment], "gpt-live", line.pending.id, null);
    return true;
  };

  return { recordBusy, handOff, closeLateLine, joinsHandOff, reclaimExchange, checkHandOff, joinsHeldLine };
};

export type HandOffs = ReturnType<typeof createHandOffs>;
